'use client';

import { Braces } from 'lucide-react';
import { useLocale } from 'next-intl';
import { usePathname, useRouter } from '@/i18n/routing';
import { type Locale } from '@/i18n/config';
import { useKeysMode, setKeysMode } from '@/i18n/use-keys-mode';
import { cn } from '@/lib/utils';
import { markLocaleTransition } from './layout/locale-transition';

const buttonClass = 'px-2 py-1.5 text-sm font-medium transition-colors md:px-3';

function activeClass(active: boolean): string {
  return active
    ? 'bg-primary text-primary-foreground'
    : 'bg-background text-muted-foreground hover:bg-muted';
}

/**
 * Language switcher. When `enableKeysMode` is set, a third toggle shows the
 * raw translation keys instead of EN/AR text (admin tool for Edit Localization
 * — read a key off the UI, paste it into search). The three options behave as
 * a mutually-exclusive group: picking a locale also exits keys mode.
 */
export function LocaleSwitcher({
  enableKeysMode = false
}: {
  enableKeysMode?: boolean;
}) {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const keysMode = useKeysMode();

  const refresh = () => {
    if (typeof router.refresh === 'function') {
      router.refresh();
    } else {
      window.location.reload();
    }
  };

  const selectLocale = (target: Locale) => {
    if (target === locale && !keysMode) return;
    setKeysMode(false);
    if (target !== locale) {
      markLocaleTransition();
      router.replace(pathname, { locale: target });
    } else {
      refresh();
    }
  };

  const toggleKeysMode = () => {
    setKeysMode(!keysMode);
    refresh();
  };

  return (
    <div className='flex items-center overflow-hidden rounded-lg border'>
      <button
        onClick={() => selectLocale('en')}
        className={cn(buttonClass, activeClass(locale === 'en' && !keysMode))}
      >
        <span className='md:hidden'>EN</span>
        <span className='hidden md:inline'>English</span>
      </button>
      <button
        onClick={() => selectLocale('ar')}
        className={cn(buttonClass, activeClass(locale === 'ar' && !keysMode))}
      >
        <span className='md:hidden'>عر</span>
        <span className='hidden md:inline'>عربي</span>
      </button>
      {enableKeysMode && (
        <button
          type='button'
          onClick={toggleKeysMode}
          aria-pressed={keysMode}
          aria-label='View translation keys'
          title='View translation keys'
          className={cn(buttonClass, 'border-s', activeClass(keysMode))}
        >
          <Braces className='size-4' aria-hidden='true' />
        </button>
      )}
    </div>
  );
}
